/**
 * `GET /portal/logs/mail` が返す運用イベントの日本語ラベル。
 *
 * 画面はもともと「1 通ごとの配送ログ（差出人・宛先・件名・配送結果）」を
 * 出す前提で書かれていたが、コントロールプレーンにその情報は無い。
 * 配送ログの実体はノードローカル DB（`delivery_events`）にあり、CP へ上がって
 * くるのは集計値と、ノードが検知した運用イベント（`agent.event.*`）だけ。
 *
 * そのため画面側を API に合わせ、「メールまわりで起きたこと」を出す表に変えた。
 * 存在しないフィールドを optional にして空欄を並べるより、何が分かるのかを
 * 正直に見せるほうが問い合わせの役に立つ。
 */
import type { MailEventLogEntry } from './api-types.js';

/** `@mail-sv/shared` の `AGENT_EVENT_KIND` に対応する説明。 */
export const MAIL_EVENT_LABEL: Record<string, string> = {
  auth_failure_burst: 'ログイン失敗の多発',
  send_rate_exceeded: '送信数の上限超過',
  spam_outbound: '送信メールが迷惑メール判定',
  relay_attempt: '不正な中継の試行',
  sender_mismatch: '差出人アドレスの不一致',
  tls_handshake_failure: '暗号化接続の失敗',
  ip_banned: '接続元 IP の遮断',
  ip_unbanned: '接続元 IP の遮断解除',
  queue_backlog: '送信待ちの滞留',
  disk_pressure: 'ディスク空き容量の逼迫',
};

export function mailEventLabel(kind: string): string {
  return MAIL_EVENT_LABEL[kind] ?? kind;
}

/** 契約者が「自分は何をすればよいか」を判断できる一文。 */
export const MAIL_EVENT_HELP: Record<string, string> = {
  auth_failure_burst:
    'パスワードの間違いが短時間に繰り返されました。心当たりが無い場合は、該当アドレスのパスワードを変更してください。',
  send_rate_exceeded:
    'プランの送信上限に達したため、一時的に送信を制限しました。大量送信が必要な場合はプラン変更をご検討ください。',
  spam_outbound:
    '送信したメールが迷惑メールと判定されました。アカウントが乗っ取られていないかご確認ください。',
  relay_attempt: '認証なしでメールを中継しようとする試みを拒否しました。お客様の操作は不要です。',
  sender_mismatch: 'ログインしたアカウントと異なる差出人での送信を拒否しました。',
  tls_handshake_failure: '暗号化接続に失敗しました。メールソフトの暗号化設定をご確認ください。',
  ip_banned: '不審なアクセスが続いたため、その接続元を一時的に遮断しました。',
  ip_unbanned: '遮断していた接続元の制限を解除しました。',
  queue_backlog: '送信待ちのメールが滞留しています。復旧作業は運営側で行います。',
  disk_pressure: 'サーバーの空き容量が減っています。復旧作業は運営側で行います。',
};

/** バッジの色。`@mail-sv/shared` の `STATUS_TONE` が解釈できる語彙に寄せる。 */
export function severityTone(severity: MailEventLogEntry['severity']): string {
  switch (severity) {
    case 'critical':
      return 'failed';
    case 'warning':
      return 'mismatch';
    default:
      return 'succeeded';
  }
}

export const SEVERITY_LABEL: Record<MailEventLogEntry['severity'], string> = {
  info: '情報',
  warning: '注意',
  critical: '重大',
};
